/**
 * NHI.tsx — Non-Human Identity Security page
 *
 * Identities are grouped by NHI scan run, newest first, with collapse/expand
 * per group — same pattern as Tickets and Auto-Fix pages.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer {
  id: string;
  name: string;
  organisation: string;
}

interface NHISummary {
  total_identities: number;
  by_risk: Record<string, number>;
  by_provider: Record<string, number>;
  hardcoded_secrets: number;
  last_scan_at: string | null;
}

interface NHIIdentity {
  id: string;
  provider: string;
  identity_type: string;
  identity_type_label: string;
  name: string;
  identity_id: string;
  risk_level: string;
  risk_reasons: string[];
  remediation: string;
  has_active_keys: boolean;
  key_count: number;
  has_console_access: boolean;
  is_mfa_enabled: boolean;
  permission_scope: string;
  days_since_last_use: number | null;
  last_used_at: string | null;
  is_resolved: boolean;
}

interface ScanGroup {
  scan_run_id: string;
  scan_date: string | null;
  scan_number: number;
  provider: string;
  total_identities: number;
  critical_count: number;
  high_count: number;
  identities: NHIIdentity[];
}

interface HardcodedSecret {
  id: string;
  file_path: string;
  line_number: number;
  secret_type: string;
  masked_value: string;
  severity: string;
  is_resolved: boolean;
}

interface ScanRun {
  id: string;
  status: string;
  providers_scanned: string[];
  identities_found: number;
  secrets_found: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  started_at: string;
  completed_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function riskColor(level: string): 'error' | 'warning' | 'info' | 'success' | 'default' {
  if (level === 'critical') return 'error';
  if (level === 'high')     return 'warning';
  if (level === 'medium')   return 'info';
  return 'default';
}

function severityColor(sev: string): 'error' | 'warning' | 'info' | 'default' {
  if (sev === 'critical') return 'error';
  if (sev === 'high')     return 'warning';
  if (sev === 'medium')   return 'info';
  return 'default';
}

function providerLabel(p: string): string {
  if (p === 'aws')   return 'AWS';
  if (p === 'azure') return 'Azure';
  if (p === 'gcp')   return 'GCP';
  return p.toUpperCase();
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtScanDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const CARD_HOVER_SX = {
  cursor: 'pointer',
  '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', transition: 'all 0.2s' },
} as const;

interface SummaryRibbonProps {
  summary: NHISummary;
  onAllIdentities: () => void;
  onRisk: (level: string) => void;
  onSecrets: () => void;
}

function SummaryRibbon({ summary, onAllIdentities, onRisk, onSecrets }: SummaryRibbonProps) {
  const cards = [
    { label: 'Total Identities',  value: summary.total_identities,          color: '#90caf9', onClick: onAllIdentities },
    { label: 'Critical',          value: summary.by_risk['critical'] ?? 0,  color: '#f44336', onClick: () => onRisk('critical') },
    { label: 'High',              value: summary.by_risk['high']     ?? 0,  color: '#ff9800', onClick: () => onRisk('high') },
    { label: 'Medium',            value: summary.by_risk['medium']   ?? 0,  color: '#29b6f6', onClick: () => onRisk('medium') },
    { label: 'Low',               value: summary.by_risk['low']      ?? 0,  color: '#66bb6a', onClick: () => onRisk('low') },
    { label: 'Hardcoded Secrets', value: summary.hardcoded_secrets,         color: '#ce93d8', onClick: onSecrets },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map(({ label, value, color, onClick }) => (
        <Grid item xs={6} sm={4} md={2} key={label}>
          <Card variant="outlined" onClick={onClick} sx={{ textAlign: 'center', ...CARD_HOVER_SX }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color }}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

interface ProviderCardsProps {
  byProvider: Record<string, number>;
  onProvider: (p: string) => void;
}

function ProviderCards({ byProvider, onProvider }: ProviderCardsProps) {
  const providers = ['aws', 'azure', 'gcp'];
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
      {providers.map(p => (
        <Card key={p} variant="outlined" onClick={() => onProvider(p)} sx={{ minWidth: 120, ...CARD_HOVER_SX }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
            <CloudIcon sx={{ fontSize: 28, color: p === 'aws' ? '#ff9900' : p === 'azure' ? '#0078d4' : '#4285f4' }} />
            <Typography variant="h5" fontWeight={700}>{byProvider[p] ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">{providerLabel(p)}</Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function IdentityRow({
  identity,
  onResolve,
}: {
  identity: NHIIdentity;
  onResolve: (id: string, resolved: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const displayLabel = identity.identity_type_label || identity.identity_type;

  return (
    <>
      <TableRow hover sx={{ opacity: identity.is_resolved ? 0.5 : 1 }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(v => !v)}>
            <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={identity.risk_level.toUpperCase()} color={riskColor(identity.risk_level)} size="small" />
        </TableCell>
        <TableCell>
          <Chip label={providerLabel(identity.provider)} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{identity.name}</Typography>
          <Typography variant="caption" color="text.secondary">{displayLabel}</Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {identity.has_active_keys && (
              <Tooltip title={`${identity.key_count} active key(s)`}>
                <Chip icon={<KeyIcon />} label={identity.key_count} size="small" color="warning" variant="outlined" />
              </Tooltip>
            )}
            {identity.is_mfa_enabled && <Chip label="MFA" size="small" color="success" />}
            {!identity.is_mfa_enabled && identity.has_active_keys && <Chip label="No MFA" size="small" color="error" variant="outlined" />}
          </Stack>
        </TableCell>
        <TableCell>
          {identity.days_since_last_use !== null
            ? <Typography variant="body2" color={identity.days_since_last_use > 90 ? 'error.main' : 'text.secondary'}>{identity.days_since_last_use}d ago</Typography>
            : <Typography variant="body2" color="text.secondary">—</Typography>
          }
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant={identity.is_resolved ? 'outlined' : 'contained'}
            color={identity.is_resolved ? 'inherit' : 'success'}
            onClick={() => onResolve(identity.id, !identity.is_resolved)}
            startIcon={<CheckCircleOutlineIcon />}
          >
            {identity.is_resolved ? 'Reopen' : 'Resolve'}
          </Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" gutterBottom>Risk Reasons</Typography>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {identity.risk_reasons.map((r, i) => (
                  <Alert key={i} severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0 }}>
                    {r}
                  </Alert>
                ))}
              </Stack>
              {identity.remediation && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Remediation</Typography>
                  <Typography variant="body2" color="text.secondary">{identity.remediation}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                Identity ID: <code>{identity.identity_id}</code>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Scan Group Card ───────────────────────────────────────────────────────────

function ScanGroupCard({
  group,
  expanded,
  onToggle,
  onResolve,
}: {
  group: ScanGroup & { identities: NHIIdentity[] };
  expanded: boolean;
  onToggle: () => void;
  onResolve: (id: string, resolved: boolean) => void;
}) {
  const dateLabel = fmtScanDate(group.scan_date);

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, cursor: 'pointer',
          bgcolor: expanded ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={700}>
              {providerLabel(group.provider)} Scan
            </Typography>
            {dateLabel && (
              <Typography variant="body2" color="text.secondary">
                &mdash; {dateLabel}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {group.scan_number > 0 && (
              <Chip label={`Scan #${group.scan_number}`} size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {group.critical_count > 0 && (
              <Chip label={`${group.critical_count} Critical`} color="error" size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {group.high_count > 0 && (
              <Chip label={`${group.high_count} High`} color="warning" size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            <Chip label={`${group.total_identities} Total`} size="small"
              sx={{ fontSize: '0.65rem' }} />
          </Stack>
        </Box>
        <ExpandMoreIcon sx={{
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: '0.2s', color: 'text.secondary', flexShrink: 0,
        }} />
      </Box>

      {/* Body */}
      <Collapse in={expanded}>
        <Divider />
        {group.identities.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No identities match the current filters.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'background.default' } }}>
                <TableCell width={40} />
                <TableCell>Risk</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Identity</TableCell>
                <TableCell>Keys / MFA</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.identities.map(identity => (
                <IdentityRow
                  key={identity.id}
                  identity={identity}
                  onResolve={onResolve}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NHI() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>(user?.customer_id ?? '');

  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<NHISummary | null>(null);
  const [scanGroups, setScanGroups] = useState<ScanGroup[]>([]);
  const [secrets, setSecrets] = useState<HardcodedSecret[]>([]);
  const [scanHistory, setScanHistory] = useState<ScanRun[]>([]);

  const [expandedScans, setExpandedScans] = useState<Set<string>>(new Set());
  const initialExpandDone = useRef(false);

  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');

  // Filters (applied client-side after fetch)
  const [filterProvider, setFilterProvider] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterScan, setFilterScan] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setSelectedCustomer(user?.customer_id ?? '');
      return;
    }
    axios
      .get<Customer[]>(`${API_BASE}/v1/customers/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => {
        setCustomers(r.data);
        if (r.data.length > 0 && !selectedCustomer) setSelectedCustomer(r.data[0].id);
      })
      .catch(() => setError('Could not load customer list'));
  }, [isAdmin, selectedCustomer, user?.customer_id]);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    [],
  );

  const loadAll = useCallback(async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    setError('');
    try {
      const identParams = new URLSearchParams({ customer_id: selectedCustomer });
      if (includeResolved) identParams.append('include_resolved', 'true');

      const secretParams = new URLSearchParams({ customer_id: selectedCustomer });
      if (includeResolved) secretParams.append('include_resolved', 'true');

      const [summaryRes, groupsRes, secretRes, histRes] = await Promise.all([
        axios.get(`${API_BASE}/v1/nhi/summary?customer_id=${selectedCustomer}`, authHeaders()),
        axios.get<ScanGroup[]>(`${API_BASE}/v1/nhi/identities?${identParams}`, authHeaders()),
        axios.get(`${API_BASE}/v1/nhi/secrets?${secretParams}`, authHeaders()),
        axios.get(`${API_BASE}/v1/nhi/scan-history?customer_id=${selectedCustomer}`, authHeaders()),
      ]);

      setSummary(summaryRes.data);
      setScanGroups(groupsRes.data ?? []);
      setSecrets(secretRes.data.secrets ?? []);
      setScanHistory(histRes.data.scan_history ?? []);
      initialExpandDone.current = false;
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to load NHI data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, selectedCustomer, includeResolved]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-expand the latest scan on first load
  useEffect(() => {
    if (!initialExpandDone.current && scanGroups.length > 0) {
      setExpandedScans(new Set([scanGroups[0].scan_run_id]));
      initialExpandDone.current = true;
    }
  }, [scanGroups]);

  // Client-side filtering: risk + provider applied within each group; scan filters which group to show
  const filteredGroups = useMemo(() => {
    return scanGroups
      .filter(g => !filterScan || g.scan_run_id === filterScan)
      .map(g => ({
        ...g,
        identities: g.identities.filter(i => {
          if (filterProvider && i.provider !== filterProvider) return false;
          if (filterRisk && i.risk_level !== filterRisk) return false;
          return true;
        }),
      }));
  }, [scanGroups, filterScan, filterProvider, filterRisk]);

  const totalIdentities = useMemo(
    () => scanGroups.reduce((sum, g) => sum + g.total_identities, 0),
    [scanGroups],
  );

  const scanOptions = useMemo(() =>
    scanGroups
      .filter(g => g.scan_run_id)
      .map(g => ({
        id: g.scan_run_id,
        label: `Scan #${g.scan_number}${g.scan_date ? ` — ${fmtDate(g.scan_date)}` : ''}`,
      })),
    [scanGroups],
  );

  function toggleScan(id: string) {
    setExpandedScans(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleScan() {
    if (!selectedCustomer) { setError('Select an organisation first'); return; }
    setScanning(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/v1/nhi/scan?customer_id=${selectedCustomer}`, {}, authHeaders());
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Scan failed');
    } finally {
      setScanning(false);
    }
  }

  async function handleResolveIdentity(id: string, resolved: boolean) {
    try {
      await axios.patch(`${API_BASE}/v1/nhi/identities/${id}/resolve`, { resolved }, authHeaders());
      setScanGroups(prev =>
        prev.map(g => ({
          ...g,
          identities: g.identities.map(i => i.id === id ? { ...i, is_resolved: resolved } : i),
        })),
      );
      const identity = scanGroups.flatMap(g => g.identities).find(i => i.id === id);
      if (identity && summary) {
        const delta = resolved ? -1 : 1;
        setSummary(s => s ? {
          ...s,
          total_identities: s.total_identities + delta,
          by_risk: { ...s.by_risk, [identity.risk_level]: (s.by_risk[identity.risk_level] ?? 0) + delta },
        } : s);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to update identity');
    }
  }

  async function handleResolveSecret(id: string, resolved: boolean) {
    try {
      await axios.patch(`${API_BASE}/v1/nhi/secrets/${id}/resolve`, { resolved }, authHeaders());
      setSecrets(prev => prev.map(s => s.id === id ? { ...s, is_resolved: resolved } : s));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to update secret');
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Non-Human Identity Security
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Discovered service accounts, API keys, and hardcoded secrets across your cloud environments.
            {summary?.last_scan_at && ` Last scan: ${fmtDate(summary.last_scan_at)}.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {isAdmin && customers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Organisation</InputLabel>
              <Select
                value={selectedCustomer}
                label="Organisation"
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.organisation || c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleScan}
            disabled={scanning || !selectedCustomer}
          >
            {scanning ? 'Scanning…' : 'Run Scan'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadAll}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {(summary?.hardcoded_secrets ?? 0) > 0 && (
        <Alert
          severity="error"
          icon={<CodeIcon />}
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setTab(2)}>View</Button>}
        >
          <strong>{summary!.hardcoded_secrets} hardcoded secret{summary!.hardcoded_secrets !== 1 ? 's' : ''}</strong> detected in source code. Rotate these credentials immediately.
        </Alert>
      )}

      {summary && (
        <SummaryRibbon
          summary={summary}
          onAllIdentities={() => { setTab(0); setFilterRisk(''); setFilterProvider(''); setFilterScan(''); }}
          onRisk={(level) => { setTab(0); setFilterRisk(level); setFilterProvider(''); setFilterScan(''); }}
          onSecrets={() => setTab(2)}
        />
      )}

      {summary && (
        <ProviderCards
          byProvider={summary.by_provider}
          onProvider={(p) => { setTab(0); setFilterProvider(p); setFilterRisk(''); setFilterScan(''); }}
        />
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Identities (${totalIdentities})`} />
        <Tab label={`Scan History (${scanHistory.length})`} />
        <Tab label={`Hardcoded Secrets (${secrets.filter(s => !s.is_resolved || includeResolved).length})`} />
      </Tabs>

      {/* ── Tab 0: Identities grouped by scan ────────────────────────────────── */}
      {tab === 0 && (
        <>
          {/* Filter bar */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
            {/* Scan filter */}
            {scanOptions.length > 1 && (
              <>
                <Chip
                  label="All Scans"
                  onClick={() => setFilterScan('')}
                  variant={filterScan === '' ? 'filled' : 'outlined'}
                  color={filterScan === '' ? 'primary' : 'default'}
                  size="small"
                />
                {scanOptions.map(s => (
                  <Chip
                    key={s.id}
                    label={s.label}
                    onClick={() => setFilterScan(s.id)}
                    variant={filterScan === s.id ? 'filled' : 'outlined'}
                    color={filterScan === s.id ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
                <Divider orientation="vertical" flexItem />
              </>
            )}

            {/* Provider filter */}
            {['', 'aws', 'azure', 'gcp'].map(p => (
              <Chip
                key={p || 'all'}
                label={p ? providerLabel(p) : 'All Providers'}
                onClick={() => setFilterProvider(p)}
                variant={filterProvider === p ? 'filled' : 'outlined'}
                color={filterProvider === p ? 'primary' : 'default'}
                size="small"
              />
            ))}
            <Divider orientation="vertical" flexItem />

            {/* Risk filter */}
            {['', 'critical', 'high', 'medium', 'low'].map(r => (
              <Chip
                key={r || 'all-risk'}
                label={r ? r.charAt(0).toUpperCase() + r.slice(1) : 'All Risk'}
                onClick={() => setFilterRisk(r)}
                variant={filterRisk === r ? 'filled' : 'outlined'}
                color={filterRisk === r ? riskColor(r) || 'primary' : 'default'}
                size="small"
              />
            ))}
            <Divider orientation="vertical" flexItem />

            <Chip
              label="Show Resolved"
              onClick={() => setIncludeResolved(v => !v)}
              variant={includeResolved ? 'filled' : 'outlined'}
              color={includeResolved ? 'secondary' : 'default'}
              size="small"
              icon={<CheckCircleOutlineIcon />}
            />
          </Stack>

          {filteredGroups.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {scanGroups.length === 0
                  ? 'No identities found. Run a scan to discover cloud identities.'
                  : 'No identities match the current filters.'}
              </Typography>
            </Paper>
          ) : (
            filteredGroups.map(group => (
              <ScanGroupCard
                key={group.scan_run_id}
                group={group}
                expanded={expandedScans.has(group.scan_run_id)}
                onToggle={() => toggleScan(group.scan_run_id)}
                onResolve={handleResolveIdentity}
              />
            ))
          )}
        </>
      )}

      {/* ── Tab 1: Scan History ──────────────────────────────────────────────── */}
      {tab === 1 && (
        scanHistory.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No scans run yet.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Providers</TableCell>
                  <TableCell>Identities</TableCell>
                  <TableCell>Secrets</TableCell>
                  <TableCell>Critical</TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>Low</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scanHistory.map(run => (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(run.completed_at ?? run.started_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={run.status}
                        size="small"
                        color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {run.providers_scanned.map(p => (
                          <Chip key={p} label={providerLabel(p)} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>{run.identities_found}</TableCell>
                    <TableCell>{run.secrets_found}</TableCell>
                    <TableCell><Typography color="error.main">{run.critical_count}</Typography></TableCell>
                    <TableCell><Typography color="warning.main">{run.high_count}</Typography></TableCell>
                    <TableCell><Typography color="info.main">{run.medium_count}</Typography></TableCell>
                    <TableCell>{run.low_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ── Tab 2: Hardcoded Secrets ─────────────────────────────────────────── */}
      {tab === 2 && (
        secrets.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No hardcoded secrets detected.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Line</TableCell>
                  <TableCell>Masked Value</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {secrets.map(secret => (
                  <TableRow key={secret.id} hover sx={{ opacity: secret.is_resolved ? 0.5 : 1 }}>
                    <TableCell>
                      <Chip label={secret.severity.toUpperCase()} color={severityColor(secret.severity)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{secret.secret_type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{secret.file_path}</Typography>
                    </TableCell>
                    <TableCell>{secret.line_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'error.main' }}>{secret.masked_value}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={secret.is_resolved ? 'outlined' : 'contained'}
                        color={secret.is_resolved ? 'inherit' : 'success'}
                        onClick={() => handleResolveSecret(secret.id, !secret.is_resolved)}
                        startIcon={<CheckCircleOutlineIcon />}
                      >
                        {secret.is_resolved ? 'Reopen' : 'Resolve'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
    </Box>
  );
}
